import {
  getRegion,
  isAPAC,
  isQuebec,
  isEMEA,
  isNA,
} from "../../generalUtils/envFns";

import { getConsentMap, loadScript } from "../../generalUtils";

import { gaEvent, GAHandlerNotFoundError } from "./utils";
import { logger, AnalyticsModuleBootstrapError } from "../../debug";
import { emitDatadogAction } from "../datadog/utils";

function consentModeIsEnabled() {
  // Consent mode is disabled for all APAC regions (including KR)
  return !isAPAC();
}

export default (config: {
  gtagId: string;
  enabled?: boolean;
  name?: string;
  debug?: boolean;
  gtagDetails?: () => any[][];
  dataLayerName?: string;
}) => {
  const GOOGLE_GA_EVENT_ACTION_NAME = "analytics_google_ga_event";
  let googleScriptLoading: ReturnType<typeof loadScript>;
  const { enabled, name, gtagId, dataLayerName } = config;

  const region = getRegion();
  let googlePath = "https://www.googletagmanager.com/";
  if (region === "ca" || region === "us") {
    googlePath = `https://shop.lululemon.com/metrics/${region}/`;
  }

  if (gtagId === undefined) {
    throw new Error(`GA4(${gtagId}) Plugin needs a tag to load`);
  }

  const dataLayerToUse = dataLayerName ?? "dataLayer";

  function gtag(..._arg) {
    if (window[dataLayerToUse] === undefined) {
      window[dataLayerToUse] = [];
    }
    window[dataLayerToUse].push(arguments);
    logger.debug(`GA4(${gtagId}) ${dataLayerToUse} event`, _arg);
  }

  // Consent related logic
  let hasConsent = false;

  const updateConsent = (
    updateType: "default" | "update",
    consented: boolean = false
  ) => {
    if (consentModeIsEnabled() === false) {
      hasConsent = true;
      logger.debug("APAC region detected — skipping consent mode");
      return;
    }

    const level = consented ? "granted" : "denied";
    const consentParams = {
      ad_user_data: level,
      ad_storage: level,
      ad_personalization: level,
      analytics_storage: level,
      wait_for_update: updateType === "default" ? 5000 : undefined,
    };
    gtag("consent", updateType, consentParams);
    hasConsent = consented;
  };
  // End of consent related logic

  const advancedConsentModeEnabled = isEMEA() || isAPAC() || isNA();

  return {
    name: name ?? "google-plugin",
    config,
    enabled: enabled ?? false,
    loaded() {
      if (googleScriptLoading !== undefined) {
        return googleScriptLoading.isReady();
      }
      return false;
    },
    ready() {
      logger.debug(`GA4(${gtagId}) Plugin is ready`);
    },
    track: ({ payload, instance }) => {
      const { properties, event } = payload;
      try {
        if (
          event === "automatedData" &&
          properties.validationResult.success === true &&
          properties.validationResult.data.payload.event.eventName
        ) {
          const automatedEventName =
            properties.validationResult.data.payload.event.eventName;
          logger.debug(
            `GA4(${gtagId}) Plugin Handling event ${automatedEventName}`,
            properties
          );
          return gaEvent(
            automatedEventName,
            properties.validationResult.data,
            (eventType, eventName, eventParams) => {
              if (
                hasConsent === false &&
                advancedConsentModeEnabled === false
              ) {
                return;
              }
              // Add anonymous ID
              eventParams.lll_tmp_aid = instance.user("traits.sessionId") || "";

              if (!hasConsent) {
                // Suppress identifiers; transaction_id is always collected for order matching
                // Always overwrite (not conditionally) so presence/absence can't signal logged-in status
                eventParams.digital_client_id = "";
                eventParams.user_id = null;
              }
              gtag(eventType, eventName, eventParams);
              emitDatadogAction(GOOGLE_GA_EVENT_ACTION_NAME, {
                gtagId,
                eventName,
                automatedEventName,
              });
            }
          );
        } else if (event === "automatedData") {
          const automatedEventName = properties?.payload?.event?.eventName;
          logger.warn(
            `GA4(${gtagId}) Plugin could not handle ${automatedEventName} event`,
            properties
          );
        }
      } catch (e) {
        const error = new AnalyticsModuleBootstrapError(
          `GA4(${gtagId}) Error`,
          {
            cause: e,
          }
        );
        if (e instanceof GAHandlerNotFoundError) {
          logger.warn(error);
        } else {
          logger.error(error);
        }
      }
    },
    initialize() {
      logger.debug("Google Plugin is loading");
      if (config?.gtagDetails) {
        (config.gtagDetails() as any[]).forEach((v: any[]) => gtag(...v));
      }

      // START Consent settings
      const { C0004 } = getConsentMap();
      let consented = false;
      const region = getRegion();
      if (region === "us" || (region === "ca" && !isQuebec())) {
        consented =
        !navigator?.["globalPrivacyControl"] &&
        (typeof C0004 === "undefined" || Number(C0004) === 1);
      }
      updateConsent("default", consented);
      // END Consent settings
      
      googleScriptLoading = loadScript(
        `${googlePath}gtag/js?id=${gtagId}${dataLayerName !== undefined ? `&l=${dataLayerName}` : ""}`
      );
      window[dataLayerToUse] = window[dataLayerToUse] || [];

      googleScriptLoading.promise.then(() => {
        gtag("get", gtagId, "session_id", (id) => {
          window.lllAnalyticsSessionId = id;
        });

        logger.debug("Google script loaded");
      });
    },
    consentUpdate({ payload }) {
      const { C0004 } = payload;
      let consented = Number(C0004) === 1;
      const region = getRegion();
      if (region === "us" || (region === "ca" && !isQuebec())) {
        consented = typeof C0004 === "undefined" || consented;
      }
      updateConsent("update", consented);
    },
  };
};
